
import React, { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const TexasCommunityPropertyLawsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    situation: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('situation', formData.situation);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d67b0mma728k8ctu1pv0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });
      
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', situation: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'What is the difference between community property and separate property in Texas?',
      answer: 'Community property includes all assets and debts acquired during marriage, regardless of whose name is on the title. Separate property includes assets owned before marriage, gifts received by one spouse, inheritances, and personal injury settlements (except lost wages). The distinction is crucial for both divorce proceedings and estate planning.'
    },
    {
      question: 'Can I sell community property without my spouse\'s consent in Texas?',
      answer: 'Generally, no. Texas law requires both spouses to consent to the sale of community real property. One spouse cannot unilaterally sell, transfer, or encumber the family homestead or other community real estate without the other spouse\'s written consent. Attempting to do so can result in the transaction being voided.'
    },
    {
      question: 'How does community property affect inheritance in Texas?',
      answer: 'When a spouse dies, their half of the community property passes according to their will or Texas intestacy laws. The surviving spouse automatically retains their half. If there\'s no will and the deceased had children from another relationship, the surviving spouse keeps their half plus one-third of the deceased\'s separate personal property.'
    },
    {
      question: 'What happens to community property debt in a Texas divorce?',
      answer: 'Community debts are divided along with community assets in a divorce. Texas courts aim for a "just and right" division, which doesn\'t always mean 50/50. Factors like earning capacity, fault in the marriage breakdown, and custody arrangements can influence how debts are allocated between spouses.'
    },
    {
      question: 'Can a prenuptial agreement override Texas community property laws?',
      answer: 'Yes, a valid prenuptial or postnuptial agreement can modify how property is characterized and divided. Couples can agree to treat certain assets as separate property or establish different division rules. However, the agreement must meet specific legal requirements to be enforceable, including full financial disclosure.'
    },
    {
      question: 'How do I prove property is separate rather than community in Texas?',
      answer: 'The burden of proof falls on the spouse claiming separate property. You\'ll need clear documentation such as purchase records predating the marriage, gift letters, inheritance documents, or records showing the property was acquired with separate funds. Commingling separate and community funds can complicate this proof.'
    },
    {
      question: 'Does Texas community property law apply to same-sex marriages?',
      answer: 'Yes, since the 2015 Supreme Court ruling in Obergefell v. Hodges, Texas community property laws apply equally to all legally married couples, regardless of gender. Same-sex married couples have the same rights and obligations regarding community property as opposite-sex couples.'
    },
    {
      question: 'What happens to community property if we move to Texas from a non-community property state?',
      answer: 'Property acquired in non-community property states generally retains its original character. However, Texas has "quasi-community property" rules that can apply to property that would have been community property if acquired in Texas. This primarily affects divorce proceedings rather than inheritance.'
    }
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Texas Community Property Laws | Divorce & Inheritance Guide | El Paso"
        description="Complete guide to Texas community property laws. Learn how community property affects divorce settlements, inheritance rights, real estate ownership, and estate planning for El Paso homeowners."
        keywords="Texas community property laws, community property divorce Texas, inheritance community property, El Paso property division, Texas marital property, separate property Texas"
        canonical="/elpaso-tx/texas-community-property-laws"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Texas Community Property Laws: How They Affect Divorce and Inheritance",
          "description": "Comprehensive guide to understanding Texas community property laws and their impact on divorce, inheritance, and real estate ownership.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "datePublished": "2025-01-15",
          "dateModified": "2025-01-15"
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4318099209f0cfbb33b6bf9335a07277.jpg"
            alt="Texas Community Property Laws"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
                Texas Property Law Guide
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Texas Community Property Laws: How They Affect <span className="text-teal-400">Divorce and Inheritance</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Understanding Texas community property laws is essential for protecting your assets during marriage, divorce, and estate planning. Learn how these laws impact your real estate and financial future.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+1-915-206-5814"
                  className="inline-flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (915) 206-5814
                </a>
                <a
                  href="/get-cash-offer-for-my-property"
                  className="inline-flex items-center px-6 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Cash Offer
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Need Help With Property Division?</h3>
              <p className="text-gray-600 mb-6">Get a free consultation about your property situation</p>
              
              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-teal-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h4>
                  <p className="text-gray-600">We'll contact you within 24 hours.</p>
                </div>
              ) : (
                <form id="hero-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <select
                    name="situation"
                    value={formData.situation}
                    onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  >
                    <option value="">Select Your Situation</option>
                    <option value="divorce">Going Through Divorce</option>
                    <option value="inheritance">Inherited Property</option>
                    <option value="estate-planning">Estate Planning</option>
                    <option value="selling">Need to Sell Property</option>
                    <option value="other">Other</option>
                  </select>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Texas is one of only nine states in the United States that follows community property laws. For the millions of married couples living in the Lone Star State, understanding these laws is not just a matter of legal curiosity—it's essential for protecting your financial interests, planning your estate, and navigating major life transitions like divorce or the death of a spouse.
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-8">
              Community property laws fundamentally change how assets and debts are owned, managed, and divided between spouses. Unlike common law states where property typically belongs to whoever earned it or whose name is on the title, Texas presumes that most property acquired during marriage belongs equally to both spouses—regardless of who earned the money or whose name appears on the deed.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              This comprehensive guide will walk you through everything El Paso homeowners need to know about Texas community property laws, including how they affect divorce proceedings, inheritance rights, real estate transactions, and estate planning strategies. Whether you're newly married, considering divorce, planning your estate, or dealing with <a href="/inherited-homes" className="text-teal-600 hover:text-teal-700 font-semibold">inherited property complications</a>, this information will help you make informed decisions about your property and financial future.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg my-8">
              <h4 className="text-lg font-bold text-gray-900 mb-2">Key Takeaway</h4>
              <p className="text-gray-700">
                In Texas, the default presumption is that all property acquired during marriage is community property, owned equally by both spouses. This applies to income, real estate, vehicles, retirement accounts, and even debts—regardless of whose name is on the account or title.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is Community Property Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Exactly Is <span className="text-teal-600">Community Property</span>?
              </h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Community property is a legal framework that treats marriage as an economic partnership. Under Texas Family Code Section 3.002, community property is defined as all property, other than separate property, acquired by either spouse during marriage. This seemingly simple definition has profound implications for how couples manage their finances and what happens when marriages end.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  The community property system originated from Spanish civil law traditions and was adopted by Texas when it was still part of Mexico. When Texas became a republic and later a state, it retained this property system, which remains in effect today. The underlying philosophy is that marriage is a partnership of equals, and both spouses contribute to the marriage—whether through earning income, managing the household, or raising children.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Examples of Community Property Include:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Wages, salaries, and bonuses earned by either spouse during marriage</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Real estate purchased during marriage, even if only one spouse's name is on the deed</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Retirement benefits and pension plans accumulated during marriage</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Business interests started or grown during the marriage</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Investment accounts funded with marital earnings</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Vehicles purchased during marriage</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Debts incurred during marriage, including credit cards and loans</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=happy%20married%20couple%20reviewing%20financial%20documents%20and%20property%20deeds%20together%20at%20kitchen%20table%20in%20modern%20southwestern%20home%20with%20warm%20natural%20lighting%20showing%20partnership%20and%20shared%20assets%20professional%20lifestyle%20photography&width=800&height=600&seq=community-prop-002&orientation=landscape"
                alt="Married couple reviewing property documents"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Separate Property Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Understanding <span className="text-teal-600">Separate Property</span> in Texas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not everything you own during marriage is community property. Texas law recognizes specific categories of separate property that belong exclusively to one spouse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Pre-Marital Assets</h3>
              <p className="text-gray-600 text-sm">Property owned before the marriage date remains separate property</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Gifts</h3>
              <p className="text-gray-600 text-sm">Gifts given specifically to one spouse, not to the couple jointly</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Inheritances</h3>
              <p className="text-gray-600 text-sm">Property inherited by one spouse, even during the marriage</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Personal Injury Awards</h3>
              <p className="text-gray-600 text-sm">Compensation for personal injuries (except lost wages)</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The distinction between community and separate property becomes critically important during divorce or when a spouse passes away. However, maintaining the separate character of property requires careful documentation and management. One of the most common issues we see with <a href="/elpaso-tx/texas-probate-guide" className="text-teal-600 hover:text-teal-700 font-semibold">Texas probate cases</a> involves disputes over whether property was truly separate or had become commingled with community assets.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">The Commingling Problem</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Commingling occurs when separate property is mixed with community property to the point where it becomes impossible to trace the original separate character. For example, if you deposit an inheritance into a joint checking account that also receives your paychecks, and you use that account for various purchases over several years, proving which funds came from the inheritance becomes extremely difficult.
              </p>

              <p className="text-gray-700 leading-relaxed">
                To protect separate property, financial advisors and attorneys often recommend keeping inherited or pre-marital assets in separate accounts, maintaining detailed records of the property's origin, and avoiding using community funds to improve or maintain separate property without proper documentation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Community Property Affects Divorce */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20mediation%20meeting%20with%20divorcing%20couple%20and%20attorneys%20discussing%20property%20division%20documents%20house%20model%20and%20financial%20papers%20on%20conference%20table%20neutral%20office%20setting%20professional%20legal%20photography&width=800&height=600&seq=community-prop-003&orientation=landscape"
                alt="Divorce property division meeting"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How Community Property Affects <span className="text-teal-600">Divorce in Texas</span>
              </h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  When a Texas marriage ends in divorce, community property must be divided between the spouses. Contrary to popular belief, Texas law does not require a 50/50 split. Instead, Texas Family Code Section 7.001 requires courts to divide community property in a manner that is "just and right," considering the rights of both parties and any children of the marriage.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  This "just and right" standard gives judges significant discretion in dividing property. Courts may consider factors such as the spouses' respective earning capacities, who has custody of the children, the health and age of each spouse, fault in the breakup of the marriage (such as adultery or cruelty), and the nature of the property being divided.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Factors Courts Consider in Property Division:</h3>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Earning capacity:</strong> A spouse with lower earning potential may receive a larger share</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Fault:</strong> Adultery, cruelty, or abandonment can affect division</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Child custody:</strong> The custodial parent often receives the family home</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Health and age:</strong> Special needs may warrant unequal division</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Waste of assets:</strong> Spending community funds recklessly before divorce</span>
                  </li>
                </ul>

                <p className="text-gray-700 leading-relaxed">
                  For couples going through divorce who own real estate, the property division process can be particularly complex. If you're facing divorce and need to sell your home quickly, understanding your options is crucial. Many divorcing couples find that <a href="/sell-distressed-property" className="text-teal-600 hover:text-teal-700 font-semibold">selling to a cash buyer</a> allows them to divide the proceeds cleanly and move forward with their lives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Estate Considerations */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Real Estate and <span className="text-teal-600">Community Property</span>
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              For most Texas families, their home is their largest asset. Understanding how community property laws apply to real estate is essential for both married couples and those going through divorce or dealing with inheritance issues.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">The Family Homestead</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Texas provides special protections for the family homestead under both the Texas Constitution and the Texas Property Code. These protections apply regardless of whether the home is community or separate property. Neither spouse can sell, transfer, or encumber the homestead without the other spouse's written consent, even if only one spouse's name is on the deed.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              This protection exists to ensure that one spouse cannot unilaterally deprive the family of their home. It also means that if you're buying a home in Texas, the title company will require both spouses to sign the deed, even if only one spouse is taking out the mortgage.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Selling Real Estate During Divorce</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              When divorcing couples need to sell their home, they have several options. They can list the property with a real estate agent and split the proceeds, one spouse can buy out the other's interest, or they can sell to a cash buyer for a quick, clean transaction. Many couples find that the traditional listing process is too slow and stressful during an already difficult time.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              If you're considering selling your home during divorce, it's important to understand that both spouses must agree to the sale and sign all necessary documents. If one spouse is uncooperative, the court may need to order the sale as part of the divorce proceedings. Working with experienced <a href="/elpaso-tx/cash-home-buyers-el-paso" className="text-teal-600 hover:text-teal-700 font-semibold">cash home buyers in El Paso</a> can simplify this process significantly.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg my-8">
              <h4 className="text-lg font-bold text-gray-900 mb-2">Important Note</h4>
              <p className="text-gray-700">
                Even after divorce, if the divorce decree awards the home to one spouse but the other spouse's name remains on the mortgage, both parties remain liable for the debt. Refinancing or selling the property is often necessary to fully separate the financial obligations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inheritance Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Community Property and <span className="text-teal-600">Inheritance Rights</span>
              </h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  When a married person dies in Texas, community property laws significantly affect how their estate is distributed. Understanding these rules is crucial for estate planning and for surviving spouses navigating the inheritance process.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Upon death, each spouse's half of the community property is treated separately. The surviving spouse automatically retains their half—it was always theirs. The deceased spouse's half passes according to their will or, if there's no will, according to Texas intestacy laws.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">If the Deceased Had a Will</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  A valid will can direct how the deceased spouse's half of community property and all of their separate property is distributed. However, Texas law does provide some protections for surviving spouses, including the right to remain in the homestead for life and certain allowances from the estate.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">If There Was No Will (Intestate Succession)</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  When someone dies without a will, <a href="/elpaso-tx/texas-intestacy-laws" className="text-teal-600 hover:text-teal-700 font-semibold">Texas intestacy laws</a> determine who inherits. For community property, if all children are also children of the surviving spouse, the surviving spouse inherits the deceased's half of community property. However, if the deceased had children from another relationship, those children inherit the deceased's half of community property, and the surviving spouse keeps only their original half.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=multigenerational%20family%20gathered%20around%20dining%20table%20discussing%20estate%20planning%20and%20inheritance%20with%20legal%20documents%20visible%20elderly%20parents%20with%20adult%20children%20warm%20home%20setting%20natural%20lighting%20professional%20family%20photography&width=800&height=600&seq=community-prop-004&orientation=landscape"
                alt="Family discussing inheritance and estate planning"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Community Property vs. Separate Property: <span className="text-teal-600">Quick Comparison</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Understanding the key differences between community and separate property is essential for protecting your assets
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Characteristic</th>
                  <th className="px-6 py-4 text-left font-semibold">Community Property</th>
                  <th className="px-6 py-4 text-left font-semibold">Separate Property</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Ownership</td>
                  <td className="px-6 py-4 text-gray-700">Owned equally by both spouses (50/50)</td>
                  <td className="px-6 py-4 text-gray-700">Owned exclusively by one spouse</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">When Acquired</td>
                  <td className="px-6 py-4 text-gray-700">During the marriage</td>
                  <td className="px-6 py-4 text-gray-700">Before marriage, or by gift/inheritance during marriage</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Management Rights</td>
                  <td className="px-6 py-4 text-gray-700">Either spouse can manage most community property</td>
                  <td className="px-6 py-4 text-gray-700">Only the owning spouse can manage</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Sale of Real Estate</td>
                  <td className="px-6 py-4 text-gray-700">Requires both spouses' consent</td>
                  <td className="px-6 py-4 text-gray-700">Only owning spouse's consent needed (except homestead)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Division in Divorce</td>
                  <td className="px-6 py-4 text-gray-700">Divided "just and right" by court</td>
                  <td className="px-6 py-4 text-gray-700">Remains with owning spouse</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">At Death</td>
                  <td className="px-6 py-4 text-gray-700">Surviving spouse keeps their half; deceased's half passes by will or intestacy</td>
                  <td className="px-6 py-4 text-gray-700">Passes entirely according to owner's will or intestacy laws</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">Creditor Claims</td>
                  <td className="px-6 py-4 text-gray-700">Community property can be used to satisfy either spouse's debts</td>
                  <td className="px-6 py-4 text-gray-700">Generally protected from other spouse's creditors</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Estate Planning Strategies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20estate%20planning%20attorney%20meeting%20with%20married%20couple%20reviewing%20trust%20documents%20and%20wills%20in%20elegant%20law%20office%20with%20bookshelves%20warm%20lighting%20showing%20wealth%20protection%20and%20family%20planning%20professional%20legal%20photography&width=800&height=600&seq=community-prop-005&orientation=landscape"
                alt="Estate planning consultation"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Estate Planning Strategies for <span className="text-teal-600">Community Property</span>
              </h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Effective estate planning in Texas requires a thorough understanding of community property laws. Without proper planning, your assets may not pass to your intended beneficiaries, and your surviving spouse could face unnecessary complications.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Community Property Agreements</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Texas allows married couples to enter into community property agreements that can convert separate property to community property or vice versa. These agreements can also include survivorship provisions, allowing community property to pass directly to the surviving spouse without probate.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Trusts and Community Property</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Many Texas couples use revocable living trusts as part of their estate plan. When properly funded, a trust can help avoid probate and provide for seamless management of assets if one spouse becomes incapacitated. Understanding the differences between <a href="/elpaso-tx/trust-vs-probate" className="text-teal-600 hover:text-teal-700 font-semibold">trusts and probate</a> is essential for making informed estate planning decisions.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Prenuptial and Postnuptial Agreements</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Couples can modify the default community property rules through prenuptial agreements (before marriage) or postnuptial agreements (during marriage). These agreements can specify that certain property will remain separate, establish different rules for property division in case of divorce, or create other arrangements that suit the couple's specific circumstances.
                </p>

                <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg mt-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Pro Tip</h4>
                  <p className="text-gray-700">
                    If you've inherited property or expect to receive an inheritance, consult with an estate planning attorney about strategies to keep it separate. Proper documentation and account management from the beginning is much easier than trying to trace and prove separate property years later.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Common Mistakes to <span className="text-teal-600">Avoid</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Understanding these pitfalls can save you significant time, money, and stress
          </p>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Assuming Title Determines Ownership</h3>
                  <p className="text-gray-700">Many people believe that if their name is on the title, they own the property outright. In Texas, property acquired during marriage is presumed to be community property regardless of whose name is on the title. This misconception can lead to serious problems during divorce or estate administration.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Commingling Separate Property</h3>
                  <p className="text-gray-700">Depositing an inheritance into a joint account or using separate funds to pay community expenses without proper documentation can convert separate property into community property. Once commingled, proving the separate character becomes extremely difficult.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Failing to Update Estate Plans After Major Life Events</h3>
                  <p className="text-gray-700">Marriage, divorce, the birth of children, and the death of a spouse all affect how community property laws apply to your situation. Failing to update wills, trusts, and beneficiary designations after these events can result in unintended consequences.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Not Understanding Debt Liability</h3>
                  <p className="text-gray-700">Community property laws also apply to debts. Debts incurred during marriage are generally community debts, meaning both spouses are liable. This can be particularly problematic if one spouse has been accumulating debt without the other's knowledge.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold">5</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Ignoring the Impact on Business Interests</h3>
                  <p className="text-gray-700">If you start or grow a business during marriage, your spouse likely has a community property interest in that business—even if they never worked there. This can complicate divorce proceedings and business succession planning.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Situations Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Special Situations and <span className="text-teal-600">Considerations</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Community property laws interact with various life circumstances in unique ways
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Military Families</h3>
              <p className="text-gray-700 mb-4">
                Military retirement benefits earned during marriage are community property, but federal law (the Uniformed Services Former Spouses' Protection Act) governs how they can be divided. Military families stationed at Fort Bliss should understand how PCS moves and deployments affect property management.
              </p>
              <a href="/elpaso-tx/fort-bliss-military-relocation" className="text-teal-600 hover:text-teal-700 font-semibold inline-flex items-center">
                Military Relocation Guide
                <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Rental Properties</h3>
              <p className="text-gray-700 mb-4">
                Rental income from community property is also community property. If you own <a href="/rental-property" className="text-teal-600 hover:text-teal-700 font-semibold">rental properties</a>, both the property and the income it generates are subject to community property rules during divorce or at death.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-bank-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Retirement Accounts</h3>
              <p className="text-gray-700 mb-4">
                Retirement benefits accumulated during marriage are community property, even if only one spouse's name is on the account. Dividing these accounts in divorce requires special court orders (QDROs) to avoid tax penalties.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-damage-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Property with Title Issues</h3>
              <p className="text-gray-700 mb-4">
                Community property complications can create <a href="/title-issues" className="text-teal-600 hover:text-teal-700 font-semibold">title issues</a> that make selling property difficult. Missing signatures, unclear ownership records, and disputes between heirs can all cloud title.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Tax Implications</h3>
              <p className="text-gray-700 mb-4">
                Community property states offer a significant tax advantage: when one spouse dies, both halves of community property receive a "stepped-up" basis for capital gains purposes. This can result in substantial tax savings when the surviving spouse sells appreciated assets.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Moving Between States</h3>
              <p className="text-gray-700 mb-4">
                If you move to Texas from a non-community property state, property acquired in your former state generally retains its original character. However, Texas has "quasi-community property" rules that can affect how such property is treated in divorce.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Middle CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need to Sell Property During Divorce or After Inheritance?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            We buy houses in any situation—divorce, inheritance, probate, or any other circumstance. Get a fair cash offer with no obligations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Your Cash Offer
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Frequently Asked <span className="text-teal-600">Questions</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Common questions about Texas community property laws
          </p>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-xl text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 py-5 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Can Help Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How TREX RE LLC Can <span className="text-teal-600">Help You</span>
              </h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  At TREX RE LLC, we understand that community property issues often arise during life's most challenging moments—divorce, the death of a spouse, or family disputes over inheritance. Our team has extensive experience helping El Paso homeowners navigate these situations with compassion and professionalism.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Whether you need to sell a home quickly during divorce proceedings, liquidate inherited property that multiple heirs own, or resolve a property situation complicated by community property laws, we can help. We buy houses in any condition and can close on your timeline—often in as little as 7-14 days.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">We Specialize In:</h3>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Divorce property sales requiring both spouses' cooperation</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Inherited properties with multiple heirs</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Properties in probate or with title complications</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Homes needing repairs that divorcing couples can't afford</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Quick sales for military families facing PCS moves</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20investor%20shaking%20hands%20with%20relieved%20homeowner%20couple%20in%20front%20of%20southwestern%20style%20home%20in%20el%20paso%20texas%20with%20sold%20sign%20warm%20golden%20hour%20lighting%20showing%20successful%20property%20transaction%20and%20relief%20professional%20photography&width=800&height=600&seq=community-prop-006&orientation=landscape"
                alt="Successful property sale"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Discuss Your Property Situation?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Whether you're dealing with divorce, inheritance, or any other community property issue, we're here to help. Get a free, no-obligation consultation today.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Your Cash Offer
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">500+</div>
              <p className="text-gray-400">Homes Purchased</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">7-14</div>
              <p className="text-gray-400">Days to Close</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">$0</div>
              <p className="text-gray-400">Fees or Commissions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Related <span className="text-teal-600">Resources</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <a href="/elpaso-tx/texas-intestacy-laws" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Texas Intestacy Laws</h3>
              <p className="text-gray-600 text-sm">Learn what happens when someone dies without a will in Texas</p>
            </a>

            <a href="/elpaso-tx/texas-probate-guide" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-scales-3-line text-xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Texas Probate Guide</h3>
              <p className="text-gray-600 text-sm">A crash course on navigating the Texas probate process</p>
            </a>

            <a href="/elpaso-tx/trust-vs-probate" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Trust vs Probate</h3>
              <p className="text-gray-600 text-sm">Compare estate planning options for Texas homeowners</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TexasCommunityPropertyLawsPage;
